import { ElementIds, Parents, Position } from 'types';
import { mixParents, calcChildrenPositions } from 'helpers/mix';
import { sortParents } from 'helpers/sort';
import BaseElementsManager from 'managers/base-elements.manager';
import { DEFAULT_ELEMENT_ID } from 'constants/misc';
import { Store } from 'stores/type';

type DropMixResult = {
  status: 'mixFail' | 'mixMixed' | 'mixNew',
  children: ElementIds,
  parents: Parents,
  positions: Position[],
};

class MixManager {
  private $store!: Store;

  public init(store: Store) {
    this.$store = store;
  }

  public getDropMixResult(): DropMixResult | null {
    if (!this.canMix) {
      return null;
    }

    const parents = this.parents;
    const children = mixParents(this.elements, parents);
    const failed = !children.length;
    const result = {
      parents,
      children,
      positions: [],
    };

    if (failed) {
      return {
        ...result,
        status: 'mixFail',
      };
    }

    const mixed = this.$store.getters.hasMixed(parents);
    if (mixed) {
      return {
        ...result,
        status: 'mixMixed',
      };
    }

    const positions = this.getChildrenPositions(children.length);
    return {
      ...result,
      status: 'mixNew',
      positions,
    };
  }
  public getBaseElementsMixResult(normalChildren: ElementIds) {
    const base = BaseElementsManager.getRecentlyUnlockedIds();
    if (!base.length) {
      return null;
    }

    const children = normalChildren.concat(base);
    const positions = this.getChildrenPositions(children.length);

    // pass all mix children with positions (to update position if base element was added)
    // also pass just new base elements to add them to library
    return {
      base,
      children,
      positions,
    };
  }

  private getChildrenPositions(count: number) {
    if (!this.father || !this.mother) return [];

    const positions = [this.father.position, this.mother.position];
    const width = this.$store.getters.workspaceBoxDimensions.width;

    return calcChildrenPositions(positions, width, count);
  }

  get elements() {
    return this.$store.getters.elements;
  }
  get parents(): Parents {
    if (!this.father || !this.mother) return [DEFAULT_ELEMENT_ID, DEFAULT_ELEMENT_ID];
    return sortParents([this.father.elementId, this.mother.elementId]);
  }
  get father() {
    return this.$store.getters.draggedElement;
  }
  get mother() {
    return this.$store.getters.draggingOverElement;
  }

  get canMix() {
    return !this.$store.getters.isMixingDisabled
        && this.$store.getters.isOver
        && this.$store.getters.isDragging
        && !this.$store.getters.isOverStatic;
  }
}

export default new MixManager();
